import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Sun, Moon, LogOut, LayoutDashboard } from 'lucide-react';
import { HashLink } from 'react-router-hash-link';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const mainNav = [
  { label: 'Appliance Plans', to: '/appliance-cover' },
  { label: 'Home Emergency', to: '/home-emergency' },
  { label: 'Boiler & Heating', to: '/boiler-cover' },
  { label: 'Pricing', to: '/#pricing' },
  { label: 'Homey AI', to: '/triage' },
  { label: 'Help', to: '/help' },
];

const Logo = () => (
  <Link to="/" className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="w-9 h-9">
      <circle cx="20" cy="20" r="20" fill="var(--dhs-primary)" />
      <path d="M12 22 L18 28 L28 14" fill="none" stroke="var(--dhs-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <div className="leading-tight">
      <div className="font-bold text-primary-dhs text-[17px] tracking-tight">Domestic Home</div>
      <div className="text-[10px] tracking-[0.22em] text-accent-dhs uppercase font-semibold">Services</div>
    </div>
  </Link>
);

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button onClick={toggle} aria-label="Toggle theme"
      className="relative w-14 h-8 rounded-full bg-surface border border-dhs flex items-center transition-colors hover:opacity-90">
      <span className="absolute inset-y-1 left-1 flex items-center gap-1 text-[10px]">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center transition-all shadow-sm ${theme === 'dark' ? 'translate-x-6 bg-accent-dhs text-white' : 'bg-primary-dhs text-white'}`}>
          {theme === 'dark' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
        </span>
      </span>
    </button>
  );
};

// Smart nav link that handles both routes and hash-scroll (no top-jump)
const NavLink = ({ to, label, active, onClick }) => {
  const cls = `px-3.5 py-2 text-sm font-semibold rounded-full transition-colors ${active ? 'bg-primary-dhs text-white' : 'text-primary-dhs/80 hover:text-primary-dhs hover:bg-surface'}`;
  if (to.includes('#')) {
    return (
      <HashLink to={to} smooth
        scroll={(el) => {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }}
        className={cls} onClick={onClick}>{label}</HashLink>
    );
  }
  return <Link to={to} className={cls} onClick={onClick}>{label}</Link>;
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-40 bg-[color:var(--dhs-bg)]/90 backdrop-blur border-b border-dhs">
      <div className="max-w-7xl mx-auto px-5">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {mainNav.map((l) => (
              <NavLink key={l.label} to={l.to} label={l.label} active={loc.pathname === l.to} />
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="rounded-full text-primary-dhs hover:bg-surface gap-1.5 h-10">
                    <LayoutDashboard className="w-4 h-4" /> {user.name}
                  </Button>
                </Link>
                <Button variant="ghost" onClick={logout} className="rounded-full text-primary-dhs hover:bg-surface gap-1.5 h-10">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="ghost" className="rounded-full text-primary-dhs hover:bg-surface gap-1.5 h-10">
                  <User className="w-4 h-4" /> Log in
                </Button>
              </Link>
            )}
            <Link to="/quote">
              <Button className="rounded-full bg-primary-dhs hover:opacity-90 text-white h-11 px-6 font-semibold">Get a quote</Button>
            </Link>
          </div>

          <button className="lg:hidden p-2 text-primary-dhs" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 border-t border-dhs">
            <nav className="flex flex-col gap-1 pt-3">
              {mainNav.map((l) => (
                <NavLink key={l.label} to={l.to} label={l.label} active={loc.pathname === l.to} onClick={() => setOpen(false)} />
              ))}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <ThemeToggle />
                {user ? (
                  <>
                    <Link to="/dashboard"><Button variant="outline" className="rounded-full">Dashboard</Button></Link>
                    <Button variant="outline" onClick={logout} className="rounded-full">Log out</Button>
                  </>
                ) : (
                  <Link to="/login"><Button variant="outline" className="rounded-full">Log in</Button></Link>
                )}
                <Link to="/quote" className="ml-auto"><Button className="rounded-full bg-primary-dhs text-white">Get a quote</Button></Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
